// src/components/ServiceCard.tsx
import React from "react";
import { Link } from "react-router-dom";

export type ServiceCardVariant = "side" | "top";

interface ServiceCardProps {
  title: string;
  description: string;
  image: string;
  link: string;
  buttonText?: string;
  variant?: ServiceCardVariant;
}

const clampStyle: React.CSSProperties = {
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

const clampStyleMulti: React.CSSProperties = {
  display: "-webkit-box",
  WebkitLineClamp: 4,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

const ServiceCard: React.FC<ServiceCardProps> = ({
  title,
  description,
  image,
  link,
  buttonText = "Learn more",
  variant = "side",
}) => {
  if (variant === "top") {
    return (
      <article className="bg-white rounded-2xl border border-lightBorder overflow-hidden shadow-sm transition-transform hover:shadow-md hover:scale-[1.01]">
        <div className="w-full p-4">
          <div
            className="mx-auto overflow-hidden rounded-xl"
            style={{ maxHeight: 320 }}
          >
            <img
              src={image}
              alt={title}
              className="object-cover w-full h-full"
              loading="lazy"
            />
          </div>
        </div>

        <div className="p-6">
          <h3
            className="text-xl md:text-2xl font-semibold text-brandNavy"
            style={clampStyle}
            title={title}
          >
            {title}
          </h3>

          <p className="mt-2 text-sm text-mutedGray" style={clampStyleMulti}>
            {description}
          </p>

          <div className="mt-6">
            <Link
              to={link}
              className="inline-flex items-center gap-2 text-sm font-medium text-brandRed"
            >
              <span>{buttonText}</span>
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      </article>
    );
  }

  // side variant
  return (
    <article className="bg-white rounded-2xl border border-lightBorder overflow-hidden shadow-sm transition-transform hover:shadow-md">
      <div className="flex flex-col md:flex-row items-start">
        {/* Image column */}
        <div className="md:w-1/2 w-full p-4 flex-shrink-0 flex items-start">
          <div
            className="overflow-hidden rounded-lg w-full"
            style={{ maxHeight: 160 }}
          >
            <img
              src={image}
              alt={title}
              className="object-cover w-full h-full"
              loading="lazy"
            />
          </div>
        </div>

        {/* Content column */}
        <div className="p-6 md:w-1/2 flex flex-col justify-start">
          <h3
            className="text-xl md:text-2xl font-semibold text-brandNavy"
            style={clampStyle}
            title={title}
          >
            {title}
          </h3>

          <p className="mt-2 text-sm text-mutedGray" style={clampStyleMulti}>
            {description}
          </p>

          <div className="mt-4">
            <Link
              to={link}
              className="inline-flex items-center gap-2 text-sm font-medium text-brandRed"
            >
              <span>{buttonText}</span>
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
};

export default ServiceCard;
